import { chartTableFlatTransformer } from '../chartTableFlatTransformer';

// Stage map defining the relationship between count keys and their labels
const stageMap = {
  application_review_total_count: 'Application review',
  interviews_total_count: 'Interviews',
  offer_total_count: 'Offer',
  hired_total_count: 'Hired',
};

/**
 * Transforms the API response into data formatted for charting.
 * Mirrors logic from `formatChart` and `chartPayload`.
 * @param {IChartTableApiResponse} response - Raw API response containing chart data.
 * @returns {Object} - Object containing `categories` (stage labels) and `series` (stage counts).
 */
export const pipelineChartTransformer = ({ response }) => {
  // Safely extract properties from response
  const { chart = [], headers, lookups, misc } = response || {};
  const { stage_mapping } = misc || {}; // Optional stage mapping for labels
  const { dt, g1, g2 } = lookups || {}; // Lookup mappings for data transformation

  // Transform raw chart data into a flat structure
  const transformedData = chartTableFlatTransformer({
    headers,
    data: chart,
    g1,
    g2,
    dt,
  });

  // Determine stage labels and data keys based on stage mapping or default stage map
  const stageKeys = stage_mapping
    ? Object.keys(stage_mapping).map((key, idx) => `s${idx + 1}_total_count`)
    : Object.keys(stageMap);

  const stageLabels = stage_mapping
    ? Object.values(stage_mapping)
    : Object.values(stageMap);

  // Initialize totals array (equivalent to `initialData` in original)
  const seriesData = Array(stageKeys.length).fill(0);

  // Accumulate counts for each stage (like `formatChart` and `chartPayload`)
  transformedData.forEach((datum) => {
    stageKeys.forEach((key, idx) => {
      // Accumulate count for each stage using either mapped or default keys
      seriesData[idx] += parseInt(datum[key], 10) || 0;
    });
  });

  // Construct output format with series and categories
  const series = stageLabels.map((label: string, idx) => {
    const defaultKeyMap = {
      Interviews: 'interviews_total_count',
      Offer: 'offer_total_count',
    };

    return {
      name: label,
      y: seriesData[idx],
      custom_id: stage_mapping
        ? `s${idx + 1}_total_count`
        : defaultKeyMap[label] || label,
    };
  });
  const categories = stageLabels; // Labels for x-axis

  return {
    categories, // x-axis labels
    series, // y-axis data for each category
  };
};
